// Client Portal — Marketing Director. Neutral slate palette with a single
// crimson accent per section (design rule: no multi-color coding).
import { useCallback, useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { marketingFetch } from "../lib/api";
import { MarketingCampaigns } from "./MarketingCampaigns";
import { MarketingApprovals } from "./MarketingApprovals";
import { MarketingSetup } from "./MarketingSetup";

const TABS = [
  ["dashboard", "Dashboard"],
  ["prospects", "Prospects"],
  ["review", "Review Queue"],
  ["campaigns", "Campaigns"],
  ["approvals", "Approvals"],
  ["setup", "Setup"],
];

export const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

export function KPI({ label, value, accent }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={`text-2xl font-bold mt-1 ${accent ? "text-red-700" : "text-slate-800"}`}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function DashboardTab({ facilityId }) {
  const [dash, setDash] = useState(null);
  const [insights, setInsights] = useState([]);
  useEffect(() => {
    marketingFetch(`/dashboard?facility_id=${facilityId}`).then(
      (r) => r.ok && setDash(r.data),
    );
    marketingFetch(`/insights?facility_id=${facilityId}`).then(
      (r) => r.ok && setInsights(r.data.insights || []),
    );
  }, [facilityId]);
  if (!dash) return <div className="p-6 text-slate-400">Loading…</div>;
  const t1 = dash.tier1;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI label="Active prospects" value={t1.active_prospects} />
        <KPI label="Awaiting approval" value={t1.drafts_awaiting_approval} />
        <KPI label="Sends this week" value={t1.sends_this_week} />
        <KPI label="Reply rate" value={fmtPct(t1.reply_rate)} accent />
        <KPI label="Replies this week" value={t1.replies_this_week} />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-700">
            Campaigns
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th>Status</th>
                <th>Sends</th>
                <th>Replies</th>
                <th>Spend / Budget</th>
              </tr>
            </thead>
            <tbody>
              {(dash.tier2.campaigns || []).map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-800">{c.name}</td>
                  <td className="text-slate-600">
                    {c.status}
                    {c.flags?.kill_threshold_breached && (
                      <span className="ml-2 text-xs text-red-700 font-semibold">
                        kill threshold
                      </span>
                    )}
                  </td>
                  <td>{c.sends}</td>
                  <td>{c.replies}</td>
                  <td className="text-slate-600">
                    {c.spend_cents != null
                      ? `$${(c.spend_cents / 100).toFixed(0)}`
                      : "—"}
                    {" / "}
                    {c.budget_cents != null
                      ? `$${(c.budget_cents / 100).toFixed(0)}`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-700">
            Variant leaderboard
          </div>
          <div className="p-4 space-y-2 text-sm">
            {(dash.tier2.variant_leaderboard || []).slice(0, 8).map((v) => (
              <div key={v.id} className="flex justify-between">
                <span className="text-slate-700 truncate mr-2">
                  {v.angle_name}{" "}
                  <span className="text-slate-400">({v.segment_key})</span>
                </span>
                <span className="text-slate-600 whitespace-nowrap">
                  {v.replies ?? 0}/{v.sends ?? 0}
                </span>
              </div>
            ))}
            {!dash.tier2.variant_leaderboard?.length && (
              <div className="text-slate-400">No send data yet.</div>
            )}
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-700 mb-2">
            Discovery run health
          </div>
          {(dash.tier3.discovery_runs || []).map((r, i) => (
            <div
              key={i}
              className="text-sm text-slate-600 flex justify-between border-t border-slate-100 py-1"
            >
              <span>
                {r.recipe_key} — {r.status}
              </span>
              <span>
                {r.inserted} in / {r.suppressed} suppressed / {r.score_failed}{" "}
                unscored
              </span>
            </div>
          ))}
          {!dash.tier3.discovery_runs?.length && (
            <div className="text-sm text-slate-400">No runs yet.</div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-700 mb-2">
            Weekly insights (advisory)
          </div>
          {insights.length ? (
            <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">
              {insights[0].brief_md || "No brief for the latest week."}
            </pre>
          ) : (
            <div className="text-sm text-slate-400">
              First weekly rollup pending.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProspectsTab({ facilityId }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [importResult, setImportResult] = useState(null);
  const load = useCallback(() => {
    const params = new URLSearchParams({ facility_id: facilityId, limit: 100 });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    marketingFetch(`/prospects?${params}`).then(
      (r) => r.ok && setRows(r.data.prospects || []),
    );
  }, [facilityId, q, status]);
  useEffect(load, [load]);

  const importCsv = async (file) => {
    const form = new FormData();
    form.append("facility_id", facilityId);
    form.append("file", file);
    const token = localStorage.getItem("hyveai_token");
    const res = await fetch(
      `${process.env.REACT_APP_MARKETING_URL || "https://marketing.hyveappliedintelligence.com"}/prospects/import-csv`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      },
    );
    setImportResult(await res.json());
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, org, email"
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
        >
          <option value="">All statuses</option>
          {[
            "candidate",
            "needs_review",
            "active",
            "disqualified",
            "suppressed",
          ].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label className="ml-auto px-3 py-2 rounded-lg bg-red-700 text-white text-sm cursor-pointer hover:bg-red-800">
          Import CSV
          <input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])}
          />
        </label>
      </div>
      {importResult && (
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700">
          Imported {importResult.inserted};{" "}
          {importResult.duplicates?.length || 0} duplicates reported;{" "}
          {importResult.errors?.length || 0} errors.
          <button
            className="ml-3 text-slate-500 underline"
            onClick={() => setImportResult(null)}
          >
            dismiss
          </button>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th>Org</th>
              <th>Role</th>
              <th>Email</th>
              <th>Score</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-800">
                  {p.display_name || "—"}
                </td>
                <td className="text-slate-600">{p.org_name || "—"}</td>
                <td className="text-slate-600">{p.role_title || "—"}</td>
                <td className="text-slate-600">{p.email || "—"}</td>
                <td
                  className={
                    p.icp_score >= 70
                      ? "text-red-700 font-semibold"
                      : "text-slate-600"
                  }
                >
                  {p.icp_score ?? "—"}
                </td>
                <td className="text-slate-600">{p.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <div className="p-6 text-slate-400 text-sm">
            No prospects yet — import a CSV or run discovery.
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewTab({ facilityId }) {
  const [queue, setQueue] = useState([]);
  const load = useCallback(() => {
    marketingFetch(`/review/queue?facility_id=${facilityId}`).then(
      (r) => r.ok && setQueue(r.data.queue || []),
    );
  }, [facilityId]);
  useEffect(load, [load]);

  const act = async (id, action) => {
    const r = await marketingFetch(`/review/${id}/${action}`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId }),
    });
    if (!r.ok) alert(r.data?.detail || "Action failed");
    load();
  };

  return (
    <div className="space-y-3">
      {queue.map((p) => (
        <div
          key={p.id}
          className="p-4 rounded-xl border border-slate-200 bg-white flex flex-wrap gap-4 items-center"
        >
          <div className="flex-1 min-w-[240px]">
            <div className="font-semibold text-slate-800">
              {p.display_name || "Unnamed"}{" "}
              <span className="text-slate-400">· {p.org_name}</span>
            </div>
            <div className="text-sm text-slate-600">
              {p.role_title} — {p.email || "no email"}
            </div>
            {p.icp_rationale && (
              <div className="text-sm text-slate-500 mt-1">
                {p.icp_rationale}
              </div>
            )}
            {p.provenance?.url && (
              <a
                href={p.provenance.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-400 underline"
              >
                source
              </a>
            )}
          </div>
          <div
            className={`text-2xl font-bold ${p.icp_score >= 70 ? "text-red-700" : "text-slate-500"}`}
          >
            {p.icp_score ?? "?"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => act(p.id, "promote")}
              className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm hover:bg-red-800"
            >
              Promote
            </button>
            <button
              onClick={() => act(p.id, "disqualify")}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm"
            >
              Disqualify
            </button>
            <button
              onClick={() => act(p.id, "suppress")}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-500 text-sm"
            >
              Suppress
            </button>
          </div>
        </div>
      ))}
      {!queue.length && (
        <div className="p-6 text-slate-400 text-sm">Review queue is empty.</div>
      )}
    </div>
  );
}

export function MarketingPortal({ facility }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const facilityId = facility.facility_id;
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Megaphone className="w-6 h-6 text-red-700" />
        <h2 className="text-xl font-bold text-slate-800">Marketing Director</h2>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${
              activeTab === key
                ? "border-red-700 text-red-700 font-semibold"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {activeTab === "dashboard" && <DashboardTab facilityId={facilityId} />}
      {activeTab === "prospects" && <ProspectsTab facilityId={facilityId} />}
      {activeTab === "review" && <ReviewTab facilityId={facilityId} />}
      {activeTab === "campaigns" && (
        <MarketingCampaigns facilityId={facilityId} />
      )}
      {activeTab === "approvals" && (
        <MarketingApprovals facilityId={facilityId} />
      )}
      {activeTab === "setup" && <MarketingSetup facilityId={facilityId} />}
    </div>
  );
}
